import {
  DESTRUCTIVE_TOOL_NAMES,
  GENERATED_TOOL_LABELS,
} from '@borradh-workspace/contracts/claire';
import type { UIMessage } from 'ai';

export { DESTRUCTIVE_TOOL_NAMES };

export interface ToolPartData {
  type: string;
  state: string;
  toolCallId: string;
  input: Record<string, unknown>;
  output?: Record<string, unknown>;
  errorText?: string;
}

export function asToolPart(
  part: UIMessage['parts'][number]
): ToolPartData | null {
  if (!part.type.startsWith('tool-')) return null;
  return part as unknown as ToolPartData;
}

/**
 * Strip the `tool-` part-type prefix, returning the tool's registered name.
 *
 * The controller registers every factory tool under its canonical
 * `{feature}_{action}` name (`meta_ads_createCampaign`), and — since wave 2
 * retired the bare-action alias — that canonical name is the ONLY thing that
 * ever flows through the AI SDK to the frontend, in part types like
 * `tool-meta_ads_createCampaign`. All frontend lookup tables
 * (`RICH_TOOL_NAMES`, `SILENT_TOOLS`, `FRIENDLY_TOOL_LABELS`, the renderer's
 * `toolName === 'X'` checks) are keyed canonically to match.
 *
 * The one exception is the legacy content-tools shim (being retired) — its
 * five tools carry bare, unprefixed names with no canonical form at all
 * (`listRecentPosts`, not `social_posts_listRecentPosts`), so this function
 * leaves an already-bare name untouched rather than inventing a prefix.
 */
export function getToolName(tp: ToolPartData): string {
  return tp.type.replace('tool-', '');
}

/**
 * A single Claire turn may create a content item and patch that same item a
 * few tool calls later. Both outputs carry a `content_clips` presentation, but
 * rendering both gives the owner two apparently independent cards for one
 * draft. Keep the newest presentation for each item within the message; while
 * streaming, the first card remains visible until its replacement arrives.
 */
export function getSupersededContentCardToolCallIds(
  parts: UIMessage['parts']
): Set<string> {
  return getSupersededContentCardToolCallIdsFromPartGroups(
    [parts],
    ['content_clips']
  );
}

/**
 * Keep only the latest card for each content item across the entire transcript.
 * A follow-up edit is stored as a new assistant message, but it still points at
 * the same immutable item ID; leaving the earlier revision mounted makes one
 * video look like two independent drafts.
 *
 * Do not memoize this from the messages array alone. The AI SDK mutates the
 * currently streaming message's parts in place, so callers intentionally run
 * this small scan on every render.
 */
export function getSupersededContentCardToolCallIdsForMessages(
  messages: UIMessage[]
): Set<string> {
  return getSupersededContentCardToolCallIdsFromPartGroups(
    messages.map((message) => message.parts),
    ['content_clips']
  );
}

/**
 * Which earlier `graphic_status`/`graphic_draft` cards, for the same content
 * item, are no longer the latest one.
 *
 * UNLIKE the video functions above, this is NOT used to hide anything — a
 * graphic edit is commonly staged on the SAME still-unrendered graphic id
 * across several turns ("change the handle" → "no, the other corner"), so
 * every one of those cards legitimately shares one `graphicId` and hiding
 * all-but-the-last would erase the visible history of what was asked for at
 * each step. It exists ONLY to stop the "selected" panel highlight
 * (`ArtifactRow`'s `isOpen`) from painting on every card that happens to
 * share that id — only the latest should ever look selected, even though
 * clicking any of them opens the same underlying artifact.
 */
export function getStaleGraphicArtifactToolCallIds(
  messages: UIMessage[]
): Set<string> {
  const partGroups = messages.map((message) => message.parts);
  const stale = getSupersededContentCardToolCallIdsFromPartGroups(partGroups, [
    'graphic_status',
    'graphic_draft',
  ]);
  // Ads share the rule. Every edit to an ad (`updateAd`, `replaceAdCreative`)
  // emits a fresh `ad_preview` for the SAME ad id, so a conversation holds one
  // row per edit — all pointing at one ad, and all lighting up when it is
  // open unless only the newest counts.
  for (const id of getSupersededAdArtifactToolCallIds(partGroups)) {
    stale.add(id);
  }
  return stale;
}

/** Every `ad_preview` card for an ad except the newest one, by tool-call id. */
function getSupersededAdArtifactToolCallIds(
  partGroups: UIMessage['parts'][]
): Set<string> {
  const latestByAdId = new Map<string, string>();
  const superseded = new Set<string>();
  for (const parts of partGroups) {
    for (const part of parts) {
      const toolPart = asToolPart(part);
      if (!toolPart) continue;
      const output = toolPart.output as
        | { adId?: unknown; presentation?: { type?: string } }
        | undefined;
      if (
        output?.presentation?.type !== 'ad_preview' ||
        typeof output.adId !== 'string'
      ) {
        continue;
      }
      const previous = latestByAdId.get(output.adId);
      if (previous) superseded.add(previous);
      latestByAdId.set(output.adId, toolPart.toolCallId);
    }
  }
  return superseded;
}

/**
 * Should this tool part's card be fully hidden from the transcript?
 *
 * ONE DEFINITION for both chat surfaces (desktop route + mobile Ask AI
 * sheet) — this exact decision (video supersession, plus collapsing a
 * superseded STAGED graphic edit) was duplicated across their two
 * `message-list.tsx` files and drifted: a fix landed in one and not the
 * other, so the desktop route kept showing a "Change staged — not
 * generated yet" notice per instruction while the mobile sheet had already
 * been fixed to collapse it. See `getStaleGraphicArtifactToolCallIds` for
 * why a staged graphic card is safe to hide (unlike a genuinely finished
 * one) — every staged notice says the exact same generic thing, so an
 * older one carries no information once a newer one exists.
 */
/** Tools that can park a memory beside the chat (see below). */
const MEMORY_PROPOSING_TOOLS = new Set(['meta_remember', 'meta_suggestMemory']);

/**
 * A memory Claire parked BESIDE the chat (web only):
 * a `memory_suggestion` carrying a `proposal`. It is not part of the
 * conversation — the side tray (`MemoryProposalTray`) shows it and the owner
 * answers it there — so the transcript and the tool trace skip it entirely.
 * While such a call is still running its output is unknown; it is skipped
 * then too, so "Saving that for later" does not flash up in the chat.
 */
export function isSideOfChatToolPart(tp: ToolPartData): boolean {
  if (typeof tp.type !== 'string') return false;
  if (!MEMORY_PROPOSING_TOOLS.has(getToolName(tp))) return false;
  if (tp.state !== 'output-available') return tp.state !== 'output-error';
  return readMemoryProposal(tp) !== null;
}

export interface MemoryProposalCard {
  toolCallId: string;
  content: string;
  conflictsWith?: { knowledgeEntryId: string; content: string };
  token: string;
  expiresAt: string;
}

/** The side-of-chat proposal a tool part carries, or null. */
export function readMemoryProposal(
  tp: ToolPartData
): MemoryProposalCard | null {
  const card = (
    tp.output as
      | {
          presentation?: {
            type?: string;
            content?: unknown;
            conflictsWith?: { knowledgeEntryId: string; content: string };
            proposal?: { token?: unknown; expiresAt?: unknown };
          };
        }
      | undefined
  )?.presentation;
  if (
    card?.type !== 'memory_suggestion' ||
    typeof card.content !== 'string' ||
    typeof card.proposal?.token !== 'string' ||
    typeof card.proposal.expiresAt !== 'string'
  ) {
    return null;
  }
  return {
    toolCallId: tp.toolCallId,
    content: card.content,
    ...(card.conflictsWith ? { conflictsWith: card.conflictsWith } : {}),
    token: card.proposal.token,
    expiresAt: card.proposal.expiresAt,
  };
}

/** The parts of an approval card that identify it, or null for any other part. */
export interface ApprovalCardRef {
  token: string;
  /** The tool the approval runs and the item it acts on: "the same change". */
  executeToolName: string;
  resourceId: string;
}

export function readApprovalCard(tp: ToolPartData): ApprovalCardRef | null {
  const card = (
    tp.output as
      | {
          presentation?: {
            type?: string;
            token?: unknown;
            executeToolName?: unknown;
            resourceId?: unknown;
          };
        }
      | undefined
  )?.presentation;
  if (
    card?.type !== 'confirmation_required' ||
    typeof card.token !== 'string' ||
    typeof card.executeToolName !== 'string' ||
    typeof card.resourceId !== 'string'
  ) {
    return null;
  }
  return {
    token: card.token,
    executeToolName: card.executeToolName,
    resourceId: card.resourceId,
  };
}

export function shouldHideToolPart(
  tp: ToolPartData,
  sets: {
    supersededContentCards: ReadonlySet<string>;
    staleGraphicArtifacts: ReadonlySet<string>;
  }
): boolean {
  if (sets.supersededContentCards.has(tp.toolCallId)) return true;
  if (isSideOfChatToolPart(tp)) return true;
  if (
    sets.staleGraphicArtifacts.has(tp.toolCallId) &&
    (tp.output as { awaitingApproval?: boolean } | undefined)
      ?.awaitingApproval === true
  ) {
    return true;
  }
  return false;
}

function getSupersededContentCardToolCallIdsFromPartGroups(
  partGroups: UIMessage['parts'][],
  presentationTypes: readonly string[]
): Set<string> {
  const latestByItemId = new Map<string, string>();
  const superseded = new Set<string>();

  for (const parts of partGroups) {
    for (const part of parts) {
      const toolPart = asToolPart(part);
      if (!toolPart) continue;
      const presentation = (
        toolPart.output as
          | { presentation?: { type?: string; itemId?: string } }
          | undefined
      )?.presentation;
      if (
        !presentation?.type ||
        !presentationTypes.includes(presentation.type) ||
        !presentation.itemId
      ) {
        continue;
      }

      const previous = latestByItemId.get(presentation.itemId);
      if (previous) superseded.add(previous);
      latestByItemId.set(presentation.itemId, toolPart.toolCallId);
    }
  }

  return superseded;
}

/**
 * Every entry below is canonical, except the truly-synthetic legacy-only
 * names (`executeVideoExport`, `updateDraftConfig`, `executeSchedulePost`,
 * `executePublishNow`) — no live tool implements them (wave 2 item D
 * retired the content-tools shim that used to), but they can still appear
 * on OLD persisted messages.
 */

/** Client tools that pause the AI until the user clicks Approve/Reject */
export const CLIENT_TOOLS = new Set(['meta_ads_confirmLaunchAd']);

/** Tools whose output should be rendered as rich components */
export const RICH_TOOL_NAMES = new Set([
  'content_createContent',
  'executeVideoExport',
  'videos_getVideoStatus',
  'videos_generateTalkingHeadQR',
  'content_patchContent',
  'meta_ads_updateAd',
  'meta_ads_replaceAdCreative',
  'meta_ads_confirmLaunchAd',
  // Window 7 — Claire chat preview cards
  'claire_showAdPreview',
  'claire_showOfferPreview',
  // Messaging campaigns — interactive review card + modal
  'campaigns_showCampaignPreview',
]);

/** Server tools that execute silently (no visible UI while running) */
export const SILENT_TOOLS = new Set([
  // Meta infrastructure — plumbing the operator doesn't need to see ever.
  // `meta_loadSkill` runs whenever the model pivots into a new skill, which
  // is several times per conversation; showing "Used 1 tool: Meta_load Skill"
  // adds noise without information.
  'meta_loadSkill',
  'meta_dispatchTour',
  // Video tools
  'content_createContent',
  'videos_generateVideoScript',
  'videos_autoSelectClips',
  'videos_listDraftClips',
  'videos_autoSelectMusic',
  'updateDraftConfig',
  // `executeVideoExport` is intentionally NOT silent — its output mounts the
  // `ProcessingStatus` loading card via the tool renderer, so the user sees
  // a progress tile as soon as the render queues.
  // Context / read-only lookups
  'context_getOrganizationContext',
  'context_listServices',
  'context_getServiceDetails',
  'context_listRecentVideos',
  'context_listOffers',
  'context_checkConnectedPages',
  'social_posts_listRecentPosts',
  'social_posts_generatePostCaption',
  'social_posts_suggestPostingTime',
  'social_posts_createSocialPostDraft',
  'social_posts_updateSocialPostDraft',
  'executeSchedulePost',
  'executePublishNow',
  // Ad tools (read-only)
  'meta_ads_checkMetaIntegration',
  'meta_ads_listCampaigns',
  'meta_ads_listRecentAds',
  'meta_ads_getAdInsights',
  'meta_ads_getCampaignInsights',
  'meta_ads_suggestAdOptimizations',
  // Ad tools (write)
  'meta_ads_generateAdCopy',
  'meta_ads_createDraftAd',
  'meta_ads_executeLaunchAd',
  // updateBudget's execute call narrates through the model; its confirm
  // call carries a confirmation_required envelope, which always renders.
  'meta_ads_updateBudget',
  // Messaging campaigns — reads + draft writes narrate through the model;
  // `showCampaignPreview` (rich card) and `launch` (confirmation) stay visible.
  'campaigns_previewAudience',
  'campaigns_listWhatsappTemplates',
  'campaigns_setMessage',
  'campaigns_createSegment',
  'campaigns_checkChannels',
]);

/** Tool types that imply Claire is asking for a destructive confirmation. */
export const CONFIRMATION_TOOLS = new Set(['meta_ads_confirmLaunchAd']);

/**
 * Tools the operator should never see in the trace. These are pure
 * infrastructure (skill loading, UI tour dispatch) — they fire constantly
 * and add no signal. The trace component skips them; the message-list
 * already hides them via `SILENT_TOOLS`.
 */
export const HIDDEN_FROM_TRACE_TOOLS = new Set([
  'meta_loadSkill',
  'meta_dispatchTour',
]);

/**
 * Labels for tools the operator sees in the trace and header are GENERATED
 * from each tool's `presentation.statusLabel` (see
 * `@borradh-workspace/contracts/claire`; regenerate with
 * `pnpm --filter @borradh-workspace/api gen:tool-labels`). The hand-written
 * map this replaces claimed to "mirror the backend" and had drifted on 38 of
 * 85 entries — including "Asking to create offer" on a tool that needs no
 * approval, which the model dutifully verbalised as a phantom permission ask.
 */

/**
 * Labels for LEGACY tools that have no descriptor to generate from — names
 * that predate the current tool set entirely (the content-tools shim that
 * wave 2 item D retired, and the earlier prod-era rename into
 * `content_createContent`). `tool-parts.test.ts` asserts these keys stay
 * disjoint from the generated map — an entry that a descriptor now covers
 * must be deleted here, not maintained in parallel. Empty for now; a truly
 * dead name (no live tool, no descriptor) gets the generic camelCase-to-
 * sentence fallback below rather than a maintained label.
 */
export const LEGACY_TOOL_LABELS: Record<string, string> = {};

const FRIENDLY_TOOL_LABELS: Record<string, string> = {
  ...LEGACY_TOOL_LABELS,
  ...GENERATED_TOOL_LABELS,
};

const TITLE_CASE_RE = /([A-Z])/g;

/**
 * Convert a raw tool name into a friendly label.
 *
 * - First tries the {@link FRIENDLY_TOOL_LABELS} override, keyed by the exact
 *   canonical name (so "meta_ads_createCampaign" becomes "Creating campaign",
 *   matching the backend's status pill) — the ONLY form the wire carries now
 *   that wave 2 retired the bare-action alias.
 * - A bare-action fallback lookup covers the legacy content-tools shim,
 *   whose tools carry no canonical prefix at all (`createDraftVideo`).
 * - Otherwise falls back to a generic transform that strips any `feature_`
 *   prefix and converts camelCase to a title-case sentence.
 *
 * The fallback is intentionally simple: when a new tool ships, it shows up
 * with a passable label until someone adds it to FRIENDLY_TOOL_LABELS.
 */
export function toolNameToLabel(name: string): string {
  // SDK recovery tool, not a factory descriptor.
  if (name === 'tool_not_available') return 'Requested action';
  if (!name) return name;

  const bareAction = name.includes('_')
    ? (name.split('_').pop() ?? name)
    : name;
  // Canonical name first — it is the precise key (two features can share a
  // bare action, e.g. `campaigns_list` vs `campaigns_segments_list`).
  if (FRIENDLY_TOOL_LABELS[name]) return FRIENDLY_TOOL_LABELS[name];
  if (FRIENDLY_TOOL_LABELS[bareAction]) return FRIENDLY_TOOL_LABELS[bareAction];

  // Generic transform on the bare action — never on the prefixed form,
  // which produces ugly mangled output like "Meta_load Skill".
  return (
    bareAction.charAt(0).toUpperCase() +
    bareAction.slice(1).replace(TITLE_CASE_RE, ' $1')
  ).trim();
}

/**
 * The header for a tool that is still working. Tool labels already read as an
 * action ("Checking Meta connection"), so a verb in front doubles it; only the
 * fallback label for an unlabelled tool ("Create Draft Video") needs one.
 */
export function inProgressToolLabel(
  verb: 'Preparing' | 'Running',
  label: string
): string {
  return /^[A-Z][a-z]+ing\b/.test(label) ? `${label}…` : `${verb} ${label}…`;
}

/**
 * Does this tool part have anything to show?
 *
 * A CARD ANSWERS FOR ITSELF. The tool says which card it wants via
 * `presentation.type`, so a tool that emits one is visible — there is no list
 * to be on.
 *
 * ONE DEFINITION, because there were three. `tool-renderer` was moved off tool
 * names, but visibility was not, so `executeLaunchAd` emitted a perfectly good
 * confirmation envelope and was hidden before the renderer ever saw it — and
 * the two copies of this rule meant fixing it in one chat left the other chat
 * still broken. "Confirm on the card above", said over nothing, three times.
 *
 * The uiState check below used to be a FOURTH copy — the desktop route's
 * `message-list.tsx` wrapped this exact function in its own local
 * `isToolPartVisible`, with a comment naming the very drift this docstring
 * describes, because a tool can be in `SILENT_TOOLS` (no chain-of-thought
 * trace badge) and still carry a `uiState` output that mounts a generic
 * CreatedCard/CreatingStatus. Folded in here so there is genuinely one
 * definition again, not three-plus-a-wrapper.
 */
export function isToolPartVisible(
  tp: ToolPartData,
  isAssistant: boolean
): boolean {
  if (!isAssistant) return false;
  if (isSideOfChatToolPart(tp)) return false;
  if (hasToolResultFailure(tp)) return true;
  const card = (tp.output as { presentation?: { type?: string } } | undefined)
    ?.presentation;
  // `none` is a tool saying it has nothing worth showing — a decision, not an
  // omission, so it must not fall through to the name list.
  if (card?.type) return card.type !== 'none';
  const name = getToolName(tp);
  if (RICH_TOOL_NAMES.has(name)) return true;
  const out = tp.output as { uiState?: unknown } | undefined;
  if (out && typeof out === 'object' && 'uiState' in out) return true;
  if (!SILENT_TOOLS.has(name)) return tp.state !== 'output-available';
  if (tp.state === 'output-error') return true;
  return false;
}

/**
 * Step status line for the tool trace — ONE definition for both chat
 * surfaces (they had drifted copies).
 *
 * The load-bearing branch is `input-available` after the stream has ended:
 * that is a tool call NOTHING executed (an undeclared/unavailable tool —
 * the model was prose-taught a tool this turn never loaded). It used to
 * render as "Awaiting your response", which reads as if CLAIRE is waiting
 * on the user — the live `createOffer` × 4 dead-air presented exactly like
 * that. A call that will never produce output is an ERROR and must say so.
 */
export function hasToolResultFailure(tp: ToolPartData): boolean {
  const data = tp.output?.data;
  const hasDataError =
    data && typeof data === 'object' && 'error' in data && Boolean(data.error);
  return Boolean(
    tp.state === 'output-error' ||
      tp.output?.ok === false ||
      tp.output?.success === false ||
      tp.output?.error ||
      hasDataError
  );
}

export function describeToolStep(
  tp: ToolPartData,
  isStreaming: boolean
): string {
  switch (tp.state) {
    case 'output-available':
      return hasToolResultFailure(tp)
        ? "Couldn't complete this action"
        : 'Complete';
    case 'output-error':
      return "Couldn't complete this action";
    case 'input-streaming':
      return 'Preparing input…';
    case 'input-available':
      return isStreaming
        ? 'Running…'
        : "Couldn't run — this action isn't available right now";
    default:
      return 'Running…';
  }
}

/** Chain-of-thought status mapping shared by both traces. */
export function mapToolStepStatus(
  state: string,
  isStreaming: boolean
): 'complete' | 'active' | 'pending' {
  if (state === 'output-available') return 'complete';
  if (state === 'output-error') return 'complete';
  if (state === 'input-streaming') return 'pending';
  // A parked call after stream end is finished (in failure) — never show it
  // as still active.
  if (state === 'input-available' && !isStreaming) return 'complete';
  return 'active';
}

export type PartRenderUnit =
  | { kind: 'single'; part: UIMessage['parts'][number]; index: number }
  | {
      kind: 'ad-row';
      items: { part: UIMessage['parts'][number]; index: number }[];
    };

/**
 * A part that renders nothing — the campaign flow's offer video / graphic
 * creatives (created with `suppressCard`) and the silent `previewCampaign`
 * read. These sit between the `createDraftAd` cards in the part stream, so
 * they must be transparent to the ad-row clustering below — otherwise they
 * break the run and the three ad cards fall back to stacked singles.
 *
 * ONE DEFINITION, same reason as `isToolPartVisible` above — this and
 * `groupParts` lived only in the desktop route's `message-list.tsx` while
 * the mobile Ask AI sheet rendered the same campaign flow's ad cards
 * stacked instead of grouped, which is not a deliberate surface difference,
 * just the two files never having been reconciled.
 */
export function isInvisibleCampaignPart(
  part: UIMessage['parts'][number]
): boolean {
  const tp = asToolPart(part);
  if (!tp) return false;
  const name = getToolName(tp);
  if (
    name !== 'createDraftVideo' &&
    name !== 'createAdGraphic' &&
    name !== 'previewCampaign'
  ) {
    return false;
  }
  return Boolean(
    (tp.input as { suppressCard?: boolean } | undefined)?.suppressCard
  );
}

/**
 * Group message parts for rendering, clustering runs of two-or-more
 * `createDraftAd` cards into a single `ad-row` unit so the campaign flow's
 * video ad + two graphic ads sit side by side instead of stacked. Suppressed
 * creative parts (the offer video / graphics, which render nothing) are
 * skipped when extending the run, so interleaved create→draft-ad sequences
 * still cluster. A lone draft ad (or any other visible part) stays a
 * `single`.
 */
export function groupParts(parts: UIMessage['parts']): PartRenderUnit[] {
  const units: PartRenderUnit[] = [];

  for (let i = 0; i < parts.length; i++) {
    const tp = asToolPart(parts[i]);
    if (tp && getToolName(tp) === 'createDraftAd') {
      const items = [{ part: parts[i], index: i }];
      let lastAd = i;
      let j = i + 1;
      while (j < parts.length) {
        const nextTp = asToolPart(parts[j]);
        if (nextTp && getToolName(nextTp) === 'createDraftAd') {
          items.push({ part: parts[j], index: j });
          lastAd = j;
          j++;
        } else if (isInvisibleCampaignPart(parts[j])) {
          // Transparent creative between two ad cards — skip, keep scanning.
          j++;
        } else {
          break;
        }
      }
      if (items.length >= 2) {
        units.push({ kind: 'ad-row', items });
        // Consume through the last ad; any skipped invisible parts in between
        // render nothing, so dropping them from the unit list is lossless.
        i = lastAd;
        continue;
      }
      units.push({
        kind: 'single',
        part: items[0].part,
        index: items[0].index,
      });
      continue;
    }
    units.push({ kind: 'single', part: parts[i], index: i });
  }

  return units;
}
